import {cards, characters, DECK_LISTS, getCard, isAlly, isBall} from './cards';
import type {StarterCharacter, DeckDefinition, DeckInput, DeckValidation, DeckValidationError} from './types';

export const HT_PERSONALITIES:Readonly<Record<StarterCharacter,string>> = Object.freeze({goku:'ss179',piccolo:'ss180',gohan:'ss181',krillin:'ss182',raditz:'ss183',vegeta:'ss184',nappa:'ss185'});
export const personalityName = (id:string):string => getCard(id).name.replace(/\s*\(Lvl.*$/,'').toLowerCase();

/** The originals were randomized. These are fixed, curated 50-Life-card decks. */
export const CURATED_DECKS:Readonly<Record<StarterCharacter,DeckDefinition>> = Object.freeze(Object.fromEntries(
  (Object.keys(DECK_LISTS) as StarterCharacter[]).map(character=>[character,Object.freeze({
    id:`starter-${character}`,name:`${characters[character].name} — Saiyan Saga`,curated:true,character,
    mpIds:Object.freeze([...characters[character].personalityIds]),
    lifeCards:Object.freeze(Object.entries(DECK_LISTS[character]).flatMap(([cardId,count])=>Array.from({length:count},()=>Object.freeze({cardId,foil:false})))),
  })]),
) as Record<StarterCharacter,DeckDefinition>);

export function validateDeck(input:DeckInput):DeckValidation {
  const errors:DeckValidationError[]=[];
  const add=(code:string,message:string,cardId?:string)=>errors.push({code,message,...cardId?{cardId}:{}});
  const lifeCards=Array.isArray(input?.lifeCards)?input.lifeCards:[];
  const mpIds=Array.isArray(input?.mpIds)?input.mpIds:[];
  const character=characters[input?.character];
  if(!character)add('CHARACTER','Choose a supported main personality.');
  if(!Array.isArray(input?.lifeCards))add('LIFE_CARDS','Life cards must be an array.');
  if(!Array.isArray(input?.mpIds))add('MP_STACK','Main personality levels must be an array.');
  if(input.mpFoils!==undefined&&(!Array.isArray(input.mpFoils)||input.mpFoils.length!==mpIds.length||input.mpFoils.some(foil=>typeof foil!=='boolean')))add('MP_FOILS','Main personality finishes must match the personality slots.');
  const lifeCount=lifeCards.length,totalCount=lifeCount+mpIds.length;
  if(totalCount<50||totalCount>75)add('DECK_SIZE','A constructed deck must contain 50–75 cards, including main personality levels.');
  const mp=mpIds.map(id=>cards[id]);
  if(!mp.length)add('MP_REQUIRED','Include a main personality.');
  const levels=mp.flatMap(c=>c?.level?[c.level]:[]);const maxLevel=Math.max(0,...levels);
  for(let i=0;i<mpIds.length;i++){
    const id=mpIds[i],c=mp[i];
    if(!c||!isAlly(id)){add('MP_PERSONALITY','Main personality slots must contain personality cards.',id);continue;}
    if(personalityName(id)!==input.character)add('MP_CHARACTER','Every main personality level must depict the selected character.',id);
    if(c.level!==i+1)add('MP_SEQUENCE','Include exactly one of each consecutive level, beginning at level 1.',id);
  }
  if(mpIds.length<3)add('MP_MIN_LEVELS','Include main personality levels 1, 2 and 3.');
  const counts=new Map<string,number>();
  const personalityLevels=new Set<string>();
  for(const entry of lifeCards){
    const id=entry?.cardId,c=cards[id];
    if(!c){add('UNKNOWN_CARD','This card has no implemented original Saiyan Saga rules.',id);continue;}
    if(entry.foil!==undefined&&typeof entry.foil!=='boolean')add('CARD_FOIL','Card finishes must be plain or foil.',id);
    counts.set(id,(counts.get(id)??0)+1);
    if(isAlly(id)){
      const key=`${personalityName(id)}:${c.level}`;
      if(personalityLevels.has(key))add('PERSONALITY_COPY_LIMIT','Include only one card for each level of a personality, including alternate high-tech versions.',id);
      personalityLevels.add(key);
      if(character&&c.type!==character.alignment)add('ALLY_ALIGNMENT','Allies must have the same alignment as your main personality.',id);
      if(personalityName(id)===input.character)add('ALLY_MAIN_PERSONALITY','Your main personality cannot also be an ally.',id);
      if((c.level??0)>maxLevel-2)add('ALLY_LEVEL','Allies must be at least two levels below your highest main personality level.',id);
    }
  }
  for(const [id,count] of counts){
    const card=cards[id]!;
    const named=character&&new RegExp(`^${character.name}(?:['’]s|\\s)`,'i').test(card.name);
    const limit=card.printedLimit??(isBall(id)||isAlly(id)?1:named?4:3);
    if(count>limit)add('COPY_LIMIT',`${card.name} allows ${limit} ${limit===1?'copy':'copies'} in this deck.`,id);
  }
  return {valid:errors.length===0,errors,lifeCount,totalCount,maxLevel};
}
